import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type MetricTone = "neutral" | "success" | "warning" | "danger";

export interface MetricStripItem {
  label: string;
  value: ReactNode;
  helper?: ReactNode;
  tone?: MetricTone;
  /** 可讀性合約：摘要格應可點擊並套用對應篩選。 */
  onClick?: () => void;
  active?: boolean;
}

export interface MetricStripProps {
  metrics: MetricStripItem[];
  className?: string;
}

const TONE_CLASSES: Record<MetricTone, string> = {
  neutral: "border-line bg-canvas text-ink",
  success: "border-done/25 bg-done-tint text-done",
  warning: "border-warn/25 bg-warn-tint text-warn",
  danger: "border-block/25 bg-block-tint text-block",
};

export function MetricStrip({ metrics, className }: MetricStripProps) {
  return (
    <div
      aria-label="營運指標"
      className={cn("grid gap-3 sm:grid-cols-2 xl:grid-cols-4", className)}
    >
      {metrics.map((metric) => {
        const content = (
          <>
            <span className="block text-sm font-medium text-ink-muted">{metric.label}</span>
            <span className="mt-2 block text-2xl font-semibold leading-none text-ink-strong">
              {metric.value}
            </span>
            {metric.helper && (
              <span className="mt-2 block text-xs leading-5 text-ink-muted">{metric.helper}</span>
            )}
          </>
        );
        const baseClassName = cn(
          "min-h-24 rounded-md border p-4",
          TONE_CLASSES[metric.tone ?? "neutral"],
          metric.active && "ring-2 ring-action/60",
        );

        if (metric.onClick) {
          return (
            <button
              key={metric.label}
              type="button"
              onClick={metric.onClick}
              aria-pressed={metric.active}
              className={cn(
                baseClassName,
                "text-left transition-colors hover:border-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action",
              )}
            >
              {content}
            </button>
          );
        }

        return (
          <div key={metric.label} className={baseClassName}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

